import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Mail, Gift } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { usePageMeta } from "@/hooks/usePageMeta";
import { SocialAuthButtons } from "@/components/auth/SocialAuthButtons";

export default function Register() {
  usePageMeta({ title: "Cadastro | Data Hire Club", description: "Crie sua conta na Data Hire Club.", noindex: true });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    companyName: "",
    userType: searchParams.get("type") === "company" ? "company" : "candidate"
  });

  // Verificar se há código de referral salvo
  useEffect(() => {
    const savedCode = localStorage.getItem("referral_code");
    if (savedCode) {
      setReferralCode(savedCode);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Preparar os metadados do usuário - sem company_name
      const userData: {
        full_name: string;
        user_type: string;
      } = {
        full_name: formData.fullName,
        user_type: formData.userType,
      };

      // Registrar o usuário com metadados consistentes
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: userData,
          emailRedirectTo: `${window.location.origin}/login`,
        }
      });

      if (error) throw error;

      // Trigger handle_new_user já cria profiles + candidates automaticamente

      // Apenas empresa precisa de INSERT manual (companies)
      if (formData.userType === 'company' && data.user?.id) {
        const { error: companyError } = await supabase.from('companies').insert({
          id: data.user.id,
          name: formData.companyName,
          profile_id: data.user.id,
        });

        if (companyError) {
          console.error("Erro ao criar empresa:", companyError);
          // Não bloquear registro se falhar (trigger já criou o profile)
        }
      }

      // Processar referral se houver código salvo
      if (referralCode && data.user?.id) {
        try {
          const { data: result, error: rpcError } = await supabase.rpc(
            "process_referral_conversion" as any,
            {
              p_referral_code: referralCode,
              p_referred_id: data.user.id,
            }
          );

          const rpcResult = result as any;
          if (!rpcError && rpcResult?.success) {
            localStorage.removeItem("referral_code");
          } else {
            console.error("Referral processing failed:", rpcError || rpcResult?.error);
          }
        } catch (referralProcessError) {
          console.error("Failed to process referral:", referralProcessError);
        }
      }

      if (data) {
        setRegisteredEmail(formData.email);
        setShowEmailModal(true);

        try {
          const { data: brevoData, error: brevoError } = await supabase.functions.invoke('create-brevo-contact', {
            body: {
              email: formData.email,
              firstName: formData.fullName,
              lastName: "",
              userId: data.user?.id,
              userType: formData.userType,
            },
          });

          if (brevoError) {
            console.error('Brevo API error:', brevoError);
          } else {
            console.log('Brevo contact created successfully:', brevoData);
          }
        } catch (brevoError) {
          console.error('Failed to create Brevo contact:', brevoError);
        }
      }
    } catch (error: unknown) {
      toast({
        title: "Erro no cadastro",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen flex flex-col md:flex-row">
        {/* Gradient Section */}
        <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-[#8B5CF6] to-[#6366F1] p-12 text-white flex-col justify-center">
          <div className="max-w-xl">
            <h1 className="text-5xl font-bold mb-6">Comece sua jornada</h1>
            <p className="text-2xl mb-8">Junte-se a milhares de profissionais e empresas.</p>
            <p className="text-xl opacity-90">
              Encontre as melhores oportunidades em dados ou os melhores talentos para sua empresa.
            </p>
          </div>
        </div>

        {/* Form Section */}
        <div className="flex-1 flex items-center justify-center p-6 bg-gray-50">
          <Card className="w-full max-w-md border-none shadow-none bg-transparent">
            <div className="mb-6">
              <Link
                to="/"
                className="inline-flex items-center text-sm text-gray-600 hover:text-[#8B5CF6] transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar para home
              </Link>
            </div>
            <CardHeader className="space-y-2">
              <CardTitle className="text-2xl md:text-3xl text-center">Criar conta</CardTitle>
              <CardDescription className="text-center">
                Cadastre-se para encontrar as melhores oportunidades em dados
              </CardDescription>
              {referralCode && (
                <div className="flex items-center justify-center gap-2 text-sm text-primary bg-primary/10 rounded-lg py-2 px-3">
                  <Gift className="h-4 w-4" />
                  <span>Convite: <strong>{referralCode}</strong></span>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {formData.userType === "candidate" && (
                <div className="mb-4">
                  <SocialAuthButtons />
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="userType">Tipo de conta</Label>
                  <Select
                    value={formData.userType}
                    onValueChange={(value) => setFormData({ ...formData, userType: value })}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Selecione o tipo de conta" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="candidate">Candidato</SelectItem>
                      <SelectItem value="company">Empresa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fullName">
                    {formData.userType === 'company' ? 'Nome do Responsável' : 'Nome completo'}
                  </Label>
                  <Input
                    id="fullName"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    placeholder={formData.userType === 'company' ? 'Nome do responsável' : 'Seu nome completo'}
                    className="bg-white"
                    required
                  />
                </div>

                {formData.userType === 'company' && (
                  <div className="space-y-2">
                    <Label htmlFor="companyName">Nome da Empresa</Label>
                    <Input
                      id="companyName"
                      value={formData.companyName}
                      onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                      placeholder="Nome da empresa"
                      className="bg-white"
                      required
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="seu@email.com"
                    className="bg-white"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    className="bg-white"
                    required
                  />
                </div>
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    checked={acceptedTerms}
                    onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                    required
                  />
                  <label htmlFor="terms" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                    Li e concordo com os{" "}
                    <Link to="/termos" className="text-primary hover:underline" target="_blank">
                      Termos de Uso
                    </Link>{" "}
                    e a{" "}
                    <Link to="/politica-de-privacidade" className="text-primary hover:underline" target="_blank">
                      Política de Privacidade
                    </Link>
                  </label>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED]"
                  disabled={loading || !acceptedTerms}
                >
                  {loading ? "Cadastrando..." : "Cadastrar"}
                </Button>
                <p className="text-center text-sm text-gray-600">
                  Já tem uma conta?{" "}
                  <Link to="/login" className="text-[#8B5CF6] hover:underline">
                    Entrar
                  </Link>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      <AlertDialog open={showEmailModal} onOpenChange={() => { }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 text-primary" />
              </div>
            </div>
            <AlertDialogTitle className="text-xl">📧 Verifique seu Email!</AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-3">
              <p>Enviamos um email de confirmação para:</p>
              <div className="bg-muted p-3 rounded-lg text-center">
                <strong className="text-primary">{registeredEmail}</strong>
              </div>
              <div className="space-y-2">
                <p className="font-medium">Para completar seu cadastro:</p>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>Acesse sua caixa de entrada</li>
                  <li>Procure o email do Data Hire Club</li>
                  <li>Clique no link de confirmação</li>
                </ol>
              </div>
              <p className="text-sm text-muted-foreground">
                💡 <strong>Dica:</strong> Se não encontrar o email, verifique a pasta de spam
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogAction
            onClick={() => {
              setShowEmailModal(false);
              navigate("/login");
            }}
            className="w-full"
          >
            Entendi, vou verificar meu email
          </AlertDialogAction>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}